import * as React from 'react';
import { ICarImageElement, IVehicle, IVehicleId } from 'data/schema';
import { userSession } from 'state/session';
import { showToast } from 'state/toast';
import { t } from '@lingui/core/macro';
import { showConfirmDialog } from 'state/confirm-dialog';
import EndComparisonDialog from 'components/EndComparisonDialog';
import { COMPARISON_CONFIG } from 'config/comparison';
import {
  activateVehicle,
  getComparingVehicles,
  getComparingVehicleIds,
  disableComparingForVehicles,
  areComparedVehiclesEqual,
} from './vehicleRegistry';

// Compare mode image element uses 'png' instead of 'source' and has an optional 'shot' property
export interface ICompareCarImageElement {
  type: 'image' | 'video';
  title: string;
  png: string;
  shot?: 'side' | 'front' | 'back';
  priority?: 'high' | 'low' | 'auto';
}

/**
 * Cherry pick images that have mode: comparison
 * @returns
 */
export function getComparisonModeImages(vehicle: IVehicle): ICarImageElement[] | undefined {
  const vehicleImages = vehicle?.carImages;
  if (!vehicleImages) {
    return undefined;
  }
  const comparisonPhotos = vehicleImages.items.filter((item) => item.mode === 'comparison');
  return comparisonPhotos;
}

/**
 * Validates whether a vehicle can be set as comparing.
 * A vehicle must have a minimum of 3 photos in carImages to be set for comparison.
 *
 * @param vehicle - The vehicle to validate
 * @returns true if the vehicle has at least 3 photos, false otherwise
 */
export function canSetComparing(vehicle?: IVehicle | string): boolean {
  if (!vehicle || typeof vehicle === 'string') {
    return false;
  }
  const vehicleImages = getComparisonModeImages(vehicle);
  if (!vehicleImages) {
    return false;
  }
  // Use convertImagesToCompareFormat to count only RENDERABLE comparison images
  // This ensures validation is consistent with what the comparison view actually displays
  const renderableImages = convertImagesToCompareFormat(vehicleImages);
  return renderableImages.length >= COMPARISON_CONFIG.MIN_IMAGES_REQUIRED;
}

/**
 * Checks if images have been fetched for the current vehicle by comparing prString
 * @returns true if carImages.prString matches session.prString
 */
export function areImagesFetchedForCurrentVehicle(): boolean {
  const session = userSession().get();
  return session.carImages?.prString === session.prString;
}

/**
 * Determines if the Compare button should be disabled in 3D mode due to insufficient comparison images
 * @returns true if in 3D 'off' mode with images fetched but vehicle lacks comparison images
 */
export function shouldDisableCompareIn3D(): boolean {
  const session = userSession().get();
  const currentVehicle = userSession().currentVehicle;

  return (
    session.activeExperience === '3D' &&
    session.comparisonMode === 'off' &&
    areImagesFetchedForCurrentVehicle() &&
    !!currentVehicle &&
    !canSetComparing(currentVehicle)
  );
}

/**
 * Determines if the Compare button should have soft-disabled styling (opacity-50)
 * @returns true if button should appear disabled but remain clickable in certain scenarios
 */
export function isCompareSoftDisabled(): boolean {
  const session = userSession().get();

  // In 3D selecting mode with unfetched images, allow clicking to trigger fetch
  if (
    session.activeExperience === '3D' &&
    session.comparisonMode === 'selecting' &&
    !areImagesFetchedForCurrentVehicle()
  ) {
    return false;
  }

  // Otherwise, soft-disable based on business rules
  const lacksImages = shouldDisableCompareIn3D() || (session.carImages?.items?.length ?? 0) < 3;
  return lacksImages || areComparedVehiclesEqual();
}

export async function validateComparisonStart() {
  let session = userSession().get();
  const activeVehicleId = session.activeVehicleId;
  let activeVehicle = activeVehicleId && session.vehicles?.[activeVehicleId];
  let [, comparingVehicle] = getComparisonVehicle();

  // comparing vehicle is tested only if we are in selecting mode
  if (!activeVehicle || (session.comparisonMode === 'selecting' && !comparingVehicle)) {
    // this should not happen in normal flow but we keep the check just in case
    return false;
  }

  // In 3D mode, fetch images if not loaded yet (for both 'off' and 'selecting' modes)
  // Only fetch if we haven't already fetched for this vehicle (check if carImages.prString matches)
  const needsImageFetch =
    session.activeExperience === '3D' &&
    (session.comparisonMode === 'off' || session.comparisonMode === 'selecting') &&
    session.prString &&
    session.carImages?.prString !== session.prString;

  if (needsImageFetch) {
    const { fetchImagesAndWait } = await import('state/session/carImages');
    const imagesLoaded = await fetchImagesAndWait();
    if (!imagesLoaded) {
      return false; // Images failed to load
    }
    // Re-fetch session after async operation to avoid stale state
    session = userSession().get();
    activeVehicle = activeVehicleId && session.vehicles?.[activeVehicleId];
    [, comparingVehicle] = getComparisonVehicle();
  }

  // Validate single vehicle has 3+ comparison images (only in 3D mode, only when starting comparison)
  if (session.activeExperience === '3D' && session.comparisonMode === 'off') {
    if (!canSetComparing(activeVehicle)) {
      showToast({
        type: 'error',
        title: t`Cannot compare vehicle`,
        message: t`Selected vehicle needs at least 3 comparison images.`,
        notificationId: 'ERROR_COMPARE_NO_IMAGES',
      });
      return false;
    }
  }

  // Validate both vehicles before entering 'on' mode (only in 3D mode)
  if (session.activeExperience === '3D' && session.comparisonMode === 'selecting') {
    if (!canSetComparing(activeVehicle) || !canSetComparing(comparingVehicle!)) {
      showToast({
        type: 'error',
        title: t`Cannot compare vehicles`,
        message: t`Both vehicles need at least 3 comparison images to compare.`,
        notificationId: 'ERROR_COMPARE_DUAL_NO_IMAGES',
      });
      return false;
    }

    // After image validation, confirm the mode switch to 2D
    const confirmed = await showConfirmDialog({
      dialogId: 'DIALOG_COMPARE_SWITCH_2D',
      title: t`Cannot compare vehicle during 3D Stream`,
      message: t`In order to compare two vehicles, you will leave the current 3D Stream. The compare images of the vehicles will be shared with the customer.`,
      confirmText: t`Switch to 2D`,
      'data-testid': 'compare-3d-active-error',
    });
    if (confirmed) {
      userSession().setValues({ activeExperience: '2D' });
      return true;
    }
    return false;
  }

  // Validate that the active vehicle has at least 3 photos before hitting compare
  // only validate this if we are upgrading comparison mode, not when switching off
  // Skip this validation for 3D mode as it's already handled above
  if (session.comparisonMode !== 'on' && session.activeExperience !== '3D') {
    const bothHaveImages = [activeVehicle, comparingVehicle].filter(Boolean).map((veh) => veh && canSetComparing(veh));
    if (bothHaveImages.includes(false)) {
      const indexOfViolation = bothHaveImages.findIndex((hasImages) => hasImages === false);
      const violatingVehicle = indexOfViolation === 0 ? activeVehicle : comparingVehicle;
      showToast({
        title: t`Cannot compare vehicle`,
        message: t`Vehicle ${String(violatingVehicle && typeof violatingVehicle === 'object' ? violatingVehicle.carDisplayName : '')} is missing comparison images and cannot be compared.`,
        type: 'error',
        notificationId: 'ERROR_COMPARE_VALIDATION',
      });
      return false;
    }
  }

  // Validate that stashed and active vehicle are not the same
  if (areComparedVehiclesEqual()) {
    showToast({
      title: t`Cannot compare vehicle`,
      message: t`You have already selected this vehicle for comparison. Please choose a different vehicle.`,
      type: 'error',
      notificationId: 'ERROR_COMPARE_SAME_VEHICLE',
    });
    return false;
  }

  return true;
}

/**
 * Convert ICarImageElement[] to ICompareCarImageElement[] for Compare component
 * Filters items to only include those with recognized comparison titles
 */
export function convertImagesToCompareFormat(items: ICarImageElement[] = []): ICompareCarImageElement[] {
  return items
    .filter((asset) => asset.mode === 'comparison')
    .map((item) => {
      // Determine shot based on title (case insensitive)
      let shot: 'side' | 'front' | 'back' | undefined;
      const titleLower = item.title.toLowerCase();

      if (titleLower.includes('front')) {
        shot = 'front';
      } else if (titleLower.includes('rear') || titleLower.includes('back')) {
        shot = 'back';
      } else if (titleLower.includes('side')) {
        shot = 'side';
      }

      return {
        type: item.type,
        title: item.title,
        source: item.source,
        png: item.source,
        shot,
        priority: item.priority,
      };
    })
    .filter((item) => item.shot !== undefined);
}

/**
 * End comparison session and select which vehicle to continue with
 * Shows a dialog with radio buttons to select the vehicle to keep active
 * @returns Selected vehicle ID or undefined if cancelled
 */
export async function endComparison(): Promise<IVehicleId | undefined> {
  const vehicles = getComparingVehicles();
  // Preserve the order: active vehicle first, then stashed vehicle
  const vehicleIds = getComparingVehicleIds();

  // Default to first vehicle (active vehicle)
  let selectedVehicleId: IVehicleId | undefined = vehicleIds[0];

  const result = await showConfirmDialog({
    dialogId: 'DIALOG_COMPARE_END',
    title: t`End comparison session`,
    message: t`Which vehicle would you like to share with your customer:`,
    confirmText: t`End comparison`,
    'data-testid': 'end-vehicle-comparison-dialog',
    children: (
      <EndComparisonDialog
        vehicleIds={vehicleIds}
        vehicles={vehicles}
        onVehicleSelected={(id) => {
          selectedVehicleId = id;
        }}
      />
    ),
  });

  if (!result) {
    // user cancelled the action
    return undefined;
  }

  // Turn off comparison mode
  userSession().setValues({ comparisonMode: 'off' });

  // Use the selected vehicle ID
  if (selectedVehicleId) {
    disableComparingForVehicles(true);
    activateVehicle(selectedVehicleId);
  }

  return selectedVehicleId;
}

export function getComparisonVehicle(): [IVehicleId, IVehicle] | [undefined, undefined] {
  const session = userSession().get();

  // if no vehicle registry or it's empty
  if (!session.vehicles || !Object.keys(session.vehicles).length) {
    return [undefined, undefined];
  }

  try {
    const values = Object.entries(session.vehicles).find(([, vehicle]) => vehicle.comparing);
    return values ? values : [undefined, undefined];
  } catch {
    return [undefined, undefined];
  }
}
